import { forwardRef, useCallback, useEffect, useMemo, useRef } from 'react'
import { useRouter } from 'next/router'
import { useEffectOnce } from 'react-use'

import { Form, useForm } from 'components/form'
import { SearchFormField, SelectArrayOption, SelectFormField } from 'components/form-field'
import { Tag } from 'api'
import { addMultiplePathSearchParameters } from 'utils/url'
import { ArticlesCollection } from 'api/strapi/articles'

import { ALL_TAGS_OPTION_SLUG, ARTICLES_SEARCH_FORM_FIELD, SORT_OPTIONS } from './consts'
import classes from './search-form.module.css'

export interface ArticleSearchFormModel {
  search: string
  tagSlug: string
  sort: string
}

interface SearchFormProps {
  tags: Tag[]
  initTag: string | null
  initSort: string | null
  initSearch: string | null
}

export const _SearchForm = forwardRef<HTMLFormElement, SearchFormProps>(
  ({ tags, initSearch, initTag, initSort }, ref) => {
    const { asPath, replace } = useRouter()
    const debounceTimeout = useRef<number | undefined>(undefined)
    const formMethods = useForm<ArticleSearchFormModel>(undefined, {
      defaultValues: {
        search: initSearch || '',
        sort: initSort || ArticlesCollection.defaultSort![0],
        tagSlug: initTag || ALL_TAGS_OPTION_SLUG,
      },
    })

    const onSearch = (data: ArticleSearchFormModel) => {
      clearTimeout(debounceTimeout.current)
      debounceTimeout.current = window.setTimeout(() => {
        replace(
          addMultiplePathSearchParameters(asPath, [
            { name: ARTICLES_SEARCH_FORM_FIELD.sort, value: data.sort, clearPrevValue: true },
            { name: ARTICLES_SEARCH_FORM_FIELD.search, value: data.search, clearPrevValue: true },
            { name: ARTICLES_SEARCH_FORM_FIELD.tagSlug, value: data.tagSlug, clearPrevValue: true },
          ]),
          undefined,
          { scroll: false, shallow: false },
        )
      }, 1000)
    }

    useEffect(() => {
      if (typeof initTag === 'string') {
        //NOTE: Need when we click on an article's tag on the articles page
        formMethods.setValue(ARTICLES_SEARCH_FORM_FIELD.tagSlug, initTag, { shouldValidate: false })
      }
    }, [formMethods, initTag])

    useEffectOnce(() => {
      const subscribe = formMethods.watch((data: ArticleSearchFormModel, event) => {
        if (event.type === 'change') {
          //call only when change fields. Don't call when change after change tag from server
          onSearch(data)
        }
      })
      return () => subscribe.unsubscribe()
    })

    const tagOptions: SelectArrayOption[] = useMemo(() => {
      const mapped = tags.map(({ id, title }) => ({ label: title, value: id }))

      return [{ value: ALL_TAGS_OPTION_SLUG, label: 'All' }, ...mapped]
    }, [tags])

    const onSubmit = useCallback(() => {}, [])

    return (
      <Form formMethods={formMethods} onSubmit={onSubmit} className={classes.form} ref={ref}>
        <SearchFormField
          name={ARTICLES_SEARCH_FORM_FIELD.search}
          placeholder="Search all posts"
          fullWidth
          className={classes.searchField}
        />
        <SelectFormField
          options={SORT_OPTIONS}
          name={ARTICLES_SEARCH_FORM_FIELD.sort}
          placeholder="Sort By"
          fullWidth
          className={classes.sortField}
        />
        <SelectFormField
          options={tagOptions}
          placeholder="Select Tag"
          name={ARTICLES_SEARCH_FORM_FIELD.tagSlug}
          fullWidth
          className={classes.tagsField}
        />
      </Form>
    )
  },
)

_SearchForm.displayName = 'SearchForm'

export const SearchForm = _SearchForm
